import { useEffect, useState } from "react";
import {
  collection,
  getCountFromServer,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../services/Firebase";

export type IdeasBadge = { count: number | null; finding: boolean };

/** Announced by the ideas feed whenever its pending count changes. */
export const IDEAS_CHANGED_EVENT = "wavegen:ideas-changed";

/**
 * Pending idea count for the sidebar. Sources are watched live (a handful of
 * documents); ideas are counted server-side, again whenever a source changes
 * (a scan finished) or the page changes, so the layout never streams topics.
 */
export function useIdeasBadge(
  workspaceId: string | null | undefined,
  pathname: string,
): IdeasBadge {
  const [sources, setSources] = useState<{
    workspaceId: string;
    version: string;
    scanning: boolean;
  } | null>(null);
  const [count, setCount] = useState<{ workspaceId: string; value: number } | null>(null);
  const [ideasChanged, setIdeasChanged] = useState(0);
  useEffect(() => {
    const recount = () => setIdeasChanged((value) => value + 1);
    window.addEventListener(IDEAS_CHANGED_EVENT, recount);
    return () => window.removeEventListener(IDEAS_CHANGED_EVENT, recount);
  }, []);

  useEffect(() => {
    if (!workspaceId) return;
    return onSnapshot(
      query(collection(db, "monitoring_sources"), where("userId", "==", workspaceId)),
      (snapshot) => {
        const docs = snapshot.docs.map((doc) => doc.data());
        setSources({
          workspaceId,
          version: snapshot.docs
            .map((doc) => `${doc.id}:${doc.data().lastPolledAt ?? ""}:${doc.data().extracting ? 1 : 0}:${doc.data().status ?? ""}:${doc.data().feedError ? 1 : 0}`)
            .join("|"),
          // A new source has not finished its first scan yet. Failed, paused or
          // broken sources never finish one, so they must not read as finding.
          scanning: docs.some(
            (source) =>
              (source.status ?? "active") === "active" &&
              !source.feedError &&
              (source.extracting || !source.lastPolledAt),
          ),
        });
      },
      () => setSources(null),
    );
  }, [workspaceId]);

  const sourcesVersion = sources && sources.workspaceId === workspaceId ? sources.version : null;
  useEffect(() => {
    if (!workspaceId || sourcesVersion === null) return;
    let cancelled = false;
    getCountFromServer(
      query(
        collection(db, "pending_topics"),
        where("userId", "==", workspaceId),
        where("status", "==", "pending"),
      ),
    )
      .then((snapshot) => {
        if (!cancelled) setCount({ workspaceId, value: snapshot.data().count });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [workspaceId, sourcesVersion, pathname, ideasChanged]);

  const value = count && count.workspaceId === workspaceId ? count.value : null;
  const scanning = !!sources && sources.workspaceId === workspaceId && sources.scanning;
  return { count: value, finding: scanning && !value };
}
